<script lang="ts" setup>
import type { Notify } from '@/types'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  note: Notify
}>()

const emits = defineEmits<{
  (e: 'remove-note', id: string): void
}>()

const router = useRouter()
const visible = ref(true)

const icon = computed(() => {
  switch (props.note.alertType) {
    case 'error': return 'mdi-alert-circle'
    case 'warning': return 'mdi-alert'
    case 'info': return 'mdi-information'
    case 'success': return 'mdi-check-circle'
    default: return 'mdi-bell'
  }
})

const dismiss = () => {
  visible.value = false
  emits('remove-note', props.note.id)
}

const handleAction = () => {
  if (props.note.action?.route) {
    router.push(props.note.action.route)
  }
  dismiss()
}

// Auto-dismiss: 4s for notifications with actions, 3s for simple ones
const timeout = props.note.action ? 4000 : 3000
setTimeout(dismiss, timeout)
</script>

<template>
  <Transition name="notify-slide">
    <div
      v-if="visible"
      class="notify-alert"
      :class="`type-${note.alertType}`"
    >
      <div class="notify-content">
        <div class="notify-icon" :class="`icon-${note.alertType}`">
          <v-icon :icon="icon" size="20" />
        </div>
        <div class="notify-text">
          <span class="notify-title">{{ note.title }}</span>
          <span v-if="note.message" class="notify-message">{{ note.message }}</span>
        </div>
        <button class="notify-close" @click="dismiss" :title="$t('common.close')">
          <v-icon icon="mdi-close" size="18" />
        </button>
      </div>
      <div v-if="note.action" class="notify-actions">
        <button class="notify-action-btn" @click="handleAction">
          {{ note.action.label }}
          <v-icon icon="mdi-arrow-right" size="16" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.notify-alert {
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 12px;
  padding: 14px 16px;
  min-width: 320px;
  max-width: 400px;
  margin-bottom: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  // Type-based accent border
  &.type-success { border-left: 3px solid #4caf50; }
  &.type-error { border-left: 3px solid #f44336; }
  &.type-warning { border-left: 3px solid #ff9800; }
  &.type-info { border-left: 3px solid #2196f3; }
}

.notify-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.notify-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;

  &.icon-success {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }
  &.icon-error {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }
  &.icon-warning {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }
  &.icon-info {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }
}

.notify-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.notify-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.notify-message {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
  line-height: 1.4;
}

.notify-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.4);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin: -4px -4px 0 0;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(200, 218, 255, 0.8);
  }
}

.notify-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.notify-action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: #78a3ff;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.2);
    border-color: rgba(120, 163, 255, 0.35);
  }
}

// Light theme
.v-theme--lightTheme {
  .notify-alert {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(28, 110, 227, 0.15);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  .notify-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .notify-message {
    color: rgba(15, 23, 42, 0.6);
  }

  .notify-close {
    color: rgba(15, 23, 42, 0.4);

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(15, 23, 42, 0.7);
    }
  }

  .notify-icon {
    &.icon-success {
      background: rgba(76, 175, 80, 0.1);
      color: #43a047;
    }
    &.icon-error {
      background: rgba(244, 67, 54, 0.1);
      color: #e53935;
    }
    &.icon-warning {
      background: rgba(255, 152, 0, 0.1);
      color: #fb8c00;
    }
    &.icon-info {
      background: rgba(33, 150, 243, 0.1);
      color: #1e88e5;
    }
  }

  .notify-action-btn {
    background: rgba(28, 110, 227, 0.08);
    border-color: rgba(28, 110, 227, 0.2);
    color: #1c6ee3;

    &:hover {
      background: rgba(28, 110, 227, 0.15);
    }
  }
}

// Slide animation
.notify-slide-enter-active {
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.notify-slide-leave-active {
  animation: slideOut 0.2s ease-in forwards;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
  .notify-slide-enter-active,
  .notify-slide-leave-active {
    animation: none;
    transition: opacity 0.2s ease;
  }

  .notify-slide-enter-from,
  .notify-slide-leave-to {
    opacity: 0;
  }
}
</style>
